import { getDictionary } from '../i18n';
import { toast } from '../toast';

/**
 * Copies the provided text to the clipboard.
 * Uses the modern Clipboard API when available, falls back to legacy execCommand for older browsers.
 *
 * @param text - The text to copy to the clipboard
 * @returns A Promise that resolves when the text is successfully copied, or rejects on failure
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  // Modern browsers: Use Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      // If Clipboard API fails, fall back to legacy method
      console.warn('Clipboard API failed, falling back to legacy method:', error);
    }
  }

  // Legacy browsers: Use execCommand with temporary textarea
  return new Promise((resolve, reject) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-999999px';
    textarea.style.top = '-999999px';
    document.body.appendChild(textarea);

    try {
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        resolve();
      } else {
        reject(new Error('Failed to copy text using execCommand'));
      }
    } catch (error) {
      document.body.removeChild(textarea);
      reject(error);
    }
  });
};

export const copyToClipboardWithToast = async (text: string, toastMessage?: string) => {
  const dictionary = getDictionary();
  await copyToClipboard(text);
  toast.success(toastMessage || dictionary.copiedToClipboard);
};
